import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { markerForCuisine, separateOverlappingCoordinates, type MapCoordinates } from "./restaurant-map-data";

export type RestaurantMapItem = {
  id: string;
  name: string;
  cuisine?: string | null;
  primaryType?: string | null;
  neighborhood?: string | null;
  city: string;
  mapsUrl: string;
  ownerName: string;
  coordinates: MapCoordinates;
  planActionLabel?: string;
};

let activeMap: L.Map | null = null;

export function destroyRestaurantMap() {
  activeMap?.remove();
  activeMap = null;
}

function popupContent(restaurant: RestaurantMapItem, onPlanRestaurant?: (restaurantId: string) => void) {
  const content = document.createElement("article");
  content.className = "map-popup-card";

  const eyebrow = document.createElement("p");
  eyebrow.className = "eyebrow";
  eyebrow.textContent = `Saved by ${restaurant.ownerName}`;

  const name = document.createElement("strong");
  name.textContent = restaurant.name;

  const details = document.createElement("span");
  details.textContent = [restaurant.neighborhood || restaurant.city, restaurant.cuisine || restaurant.primaryType].filter(Boolean).join(" · ");

  const link = document.createElement("a");
  link.href = restaurant.mapsUrl;
  link.target = "_blank";
  link.rel = "noreferrer";
  link.textContent = "Open in Google Maps ↗";

  content.append(eyebrow, name, details, link);
  if (restaurant.planActionLabel && onPlanRestaurant) {
    const action = document.createElement("button");
    action.type = "button";
    action.className = "map-plan-button";
    action.textContent = restaurant.planActionLabel;
    action.addEventListener("click", () => onPlanRestaurant(restaurant.id));
    content.append(action);
  }
  return content;
}

export function mountRestaurantMap(
  container: HTMLElement,
  restaurants: RestaurantMapItem[],
  onLoadingError: () => void,
  onPlanRestaurant?: (restaurantId: string) => void,
) {
  destroyRestaurantMap();
  const map = L.map(container, {
    zoomControl: true,
    scrollWheelZoom: false,
    keyboard: true,
    tapHold: true,
  });
  activeMap = map;

  let tileErrors = 0;
  let failed = false;
  const tiles = L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
    subdomains: "abcd",
    maxZoom: 20,
  });
  tiles.on("tileerror", () => {
    tileErrors += 1;
    if (!failed && tileErrors >= 3) {
      failed = true;
      onLoadingError();
    }
  });
  tiles.addTo(map);

  const points = separateOverlappingCoordinates(restaurants);
  const bounds: L.LatLngExpression[] = [];
  for (const { item: restaurant, coordinates } of points) {
    const cuisine = markerForCuisine(restaurant.cuisine, restaurant.primaryType);
    const markerIcon = L.divIcon({
      className: "cuisine-map-marker-wrap",
      html: `<span class="cuisine-map-marker" style="--marker-color:${cuisine.color}" aria-hidden="true"><span>${cuisine.glyph}</span></span>`,
      iconSize: [46, 52],
      iconAnchor: [23, 49],
      popupAnchor: [0, -46],
    });
    const point: L.LatLngExpression = [coordinates.latitude, coordinates.longitude];
    const marker = L.marker(point, {
      icon: markerIcon,
      keyboard: true,
      title: restaurant.name,
      alt: `${restaurant.name}, ${cuisine.label}`,
      riseOnHover: true,
    }).addTo(map);
    marker.bindPopup(popupContent(restaurant, onPlanRestaurant), { closeButton: true, minWidth: 210, maxWidth: 290 });
    marker.on("focus", () => marker.openPopup());
    bounds.push(point);
  }

  if (bounds.length === 1) map.setView(bounds[0], 13);
  else map.fitBounds(L.latLngBounds(bounds), { padding: [54, 54], maxZoom: 14 });
  window.setTimeout(() => map.invalidateSize(), 0);
  return map;
}
